<template>
    <guest-invoices-setup-base-card v-if="reservation">
        <template v-slot:card-body>
            <b-row>
                <b-col>
                    <b>BOOKING METHOD:</b>
                </b-col>
                <b-col class="text-right">
                    <span class="mr-1">
                        {{ reservation.booking_method_name }}
                    </span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>PAYMENT METHOD:</b>
                </b-col>
                <b-col class="text-right">
                    <span class="mr-1">{{ reservation.payment_method }}</span>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b>REFERRER:</b>
                </b-col>
                <b-col cols="auto">
                    <span v-if="reservation.booking_method_name != 'NextPax'">
                        <b-form-select
                            v-if="referrer.isEdit && selectedCell === 'referrer'"
                            v-click-outside="handleOutsideClick"
                            :autofocus="true"
                            v-model="reservation.referrer"
                            :options="referrerOptions"
                            @change="handleReferrerChange"
                        />
                        <span
                            v-else
                            type="button"
                            class="pr-1 text-primary guest-invoices-setup-base-card__clickable-cell"
                            @click="editCellHandler('referrer')"
                        >
                            {{ currentReferrer }}
                        </span>
                    </span>
                    <span v-else class="mr-1">{{ reservation.referrer }}</span>
                </b-col>
            </b-row>
            <b-row v-if="reservation.survey_data.display_guest">
                <b-col>
                    <b>{{ reservation.survey_data.title.toUpperCase() }}:</b>
                </b-col>
                <b-col cols="auto">
                    <b-form-select
                        v-if="surveyData.isEdit && selectedCell === 'surveyData'"
                        v-click-outside="handleOutsideClick"
                        :autofocus="true"
                        v-model="reservation.inquiry_index"
                        :options="surveyDataOptions"
                        @change="handleSurveyDataChange"
                    />
                    <span
                        v-else
                        type="button"
                        class="pr-1 text-primary guest-invoices-setup-base-card__clickable-cell"
                        @click="editCellHandler('surveyData')"
                    >
                        {{ currentSurveyData }}
                    </span>
                </b-col>
            </b-row>
            <span v-else>
                <b-row>
                    <b-col><b>BOOKING SURVEY:</b></b-col>
                    <b-col cols="auto" class="text-right">
                        <span class="mr-1">N/A</span>
                    </b-col>
                </b-row>
            </span>
            <b-row>
                <b-col>
                    <b>RENTAL AGREEMENT:</b>
                </b-col>
                <b-col cols="auto">
                    <reservation-digital-signature-icon
                        class="mr-1"
                        v-if="reservation.sign_status !== 'N/A'"
                        @sign-click="$bvModal.show(requestSignatureModalId)"
                        :reservation="reservation"
                    />
                    <span v-else class="mr-1">N/A</span>
                    <digital-signature-request-modal
                        :modalId="requestSignatureModalId"
                        :initialValues="reservation"
                        :onUpdate="refreshReservation"
                    />
                </b-col>
            </b-row>
            <b-row v-if="reservation.nextpax_invoice_source">
                <b-col><b>INVOICE SOURCE:</b></b-col>
                <b-col cols="auto">
                    <a class="mr-1" href="#" @click="$bvModal.show(invoiceSourceModalId)"> Show </a>
                </b-col>
            </b-row>
            <div v-if="reservation.hosted_form_data" class="">
                <b-row v-for="item in reservation.hosted_form_data" :key="item.title">
                    <b-col
                        ><b>{{ item.title.toUpperCase() }}</b></b-col
                    >
                    <b-col cols="auto">
                        {{ item.displayValue }}
                    </b-col>
                </b-row>
            </div>
            <modal :id="manageReferrersModalId" title="Manage Referrers" ok-title="Close" ok-only>
                <busy-modal :show="referrerModalBusy"></busy-modal>
                <b-row>
                    <b-col>
                        <b>Referrer</b>
                        <hr class="m-0 mt-2" />
                    </b-col>
                </b-row>
                <b-row v-for="r in referrers" :key="r.id">
                    <b-col>
                        {{ r.referer }}
                    </b-col>
                    <b-col cols="auto">
                        <b-button title="Delete" variant="outline-primary" @click="showDeleteReferrer(r.id)">
                            <font-awesome-icon :icon="['fas', 'minus']" />
                        </b-button>
                    </b-col>
                </b-row>
            </modal>
            <validation-observer ref="add_new_referrer" v-slot="{ handleSubmit, meta }">
                <modal
                    :id="addNewReferrerModalId"
                    title="Add New Referrer"
                    ok-title="Add"
                    :ok-disabled="!meta.valid || referrerModalBusy"
                    @ok.prevent="handleSubmit(_,addReferrer)"
                >
                    <busy-modal :show="referrerModalBusy"></busy-modal>
                    <b-row>
                        <b-col>
                            <validation-provider name="newReferrer" v-slot="{ errors }" rules="required">
                                <form-input
                                    v-model="newReferrer"
                                    floating-label="Referrer"
                                    :error="errors[0]"
                                    @input="handleChange"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                </modal>
            </validation-observer>
            <modal :id="deleteReferrerModalId" title="Delete Referrer" @ok="deleteReferrer">
                Are you sure you want to delete this referrer?
            </modal>
            <modal :id="invoiceSourceModalId" title="Invoice Source" ok-title="Ok" ok-only>
                <span v-html="reservation.nextpax_invoice_source" />
            </modal>
        </template>
    </guest-invoices-setup-base-card>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import Modal from '../../../../generic/modal/modal.vue';
import BusyModal from '../../../../generic/busy-modal/busy-modal.vue';
import FormInput from '../../../../generic/form-input/form-input.vue';
import {
    GUEST_REFERRER_DELETE_ENDPOINT,
    GUEST_REFERRER_LIST_ENDPOINT,
    GUEST_RESERVATION_ENDPOINT,
} from '../../../../../endpoints.js';
import { formatResponseError } from '../../../../../utils/api.js';
import DigitalSignatureRequestModal from '../../../../common/digital-signature-request-modal.vue';
import ReservationDigitalSignatureIcon from '../../../../common/reservation-digital-signature-icon.vue';
import GuestInvoicesSetupBaseCard from './base-card.vue';

export default {
    name: 'GuestInvoicesSetupForthCard',
    components: {
        Modal,
        BusyModal,
        FormInput,
        GuestInvoicesSetupBaseCard,
        DigitalSignatureRequestModal,
        ReservationDigitalSignatureIcon,
    },
    data() {
        return {
            selectedCell: null,
            referrerModalBusy: false,
            surveyData: { previous: null, isEdit: false },
            referrer: { previous: null, isEdit: false },
            requestSignatureModalId: `request-signature-modal-id-${this.reservationId}`,
            manageReferrersModalId: `manage-referrers-modal-id-${this.reservationId}`,
            deleteReferrerModalId: `delete-referrer-modal-id-${this.reservationId}`,
            addNewReferrerModalId: `add-new-referrer-modal-id-${this.reservationId}`,
            invoiceSourceModalId: `invoice-source-modal-id-${this.reservationId}`,
            deleteReferrerId: null,
            referrers: [],
            newReferrer: '',
        };
    },
    inject: ['showSuccessToast', 'showErrorToast'],
    computed: {
        ...mapGetters('guest', ['reservation', 'referrerOptions', 'surveyDataOptions']),
        guestId() {
            return parseInt(this.$route.params.id);
        },
        reservationId() {
            return parseInt(this.$route.query.reservation);
        },
        currentReferrer() {
            return (
                this.referrerOptions.find((r) => r.value == this.reservation.referrer)?.text ||
                this.reservation.referrer
            );
        },
        currentSurveyData() {
            return this.surveyDataOptions.find((r) => r.value == this.reservation.inquiry_index)?.text || 'Not set';
        },
    },
    methods: {
        handleOutsideClick(event) {
            this.referrer.isEdit = false;
            this.surveyData.isEdit = false;
            this.selectedCell = null;
        },
        editCellHandler(name) {
            this[name].isEdit = true;
            this.selectedCell = name;
            if (name == 'referrer') this.referrer.previous = this.reservation.referrer;
            if (name == 'surveyData') this.surveyData.previous = this.reservation.inquiry_index;
        },
        async handleReferrerChange(value) {
            const previous = this.referrer.previous;
            const newReferrer = value;
            if (newReferrer == 'Manage Referrers') {
                this.$bvModal.show(this.manageReferrersModalId);
                this.reservation.referrer = previous;
                this.fetchReferrers();
                this.handleOutsideClick();
                return;
            } else if (newReferrer == 'Add new referrer ...') {
                this.$bvModal.show(this.addNewReferrerModalId);
                this.reservation.referrer = previous;
                this.handleOutsideClick();
                return;
            }
            try {
                this.$emit('loading', true);
                if (newReferrer != previous) {
                    await API.patch(GUEST_RESERVATION_ENDPOINT(this.reservationId, this.guestId), {
                        referrer: newReferrer,
                    });
                    this.showSuccessToast({
                        text: 'Referrer successfully updated.',
                    });
                }
            } catch (e) {
                this.reservation.referrer = previous;
                const { generic } = formatResponseError(e);
                this.showErrorToast({
                    text: generic || 'Could not change referrer.',
                });
            } finally {
                this.$emit('loading', false);
                this.handleOutsideClick();
            }
        },
        async handleSurveyDataChange(value) {
            const previous = this.surveyData.previous;
            const newSurveyData = value;
            try {
                this.$emit('loading', true);
                if (newSurveyData != previous) {
                    await API.patch(GUEST_RESERVATION_ENDPOINT(this.reservationId, this.guestId), {
                        inquiry_index: newSurveyData,
                    });
                    this.showSuccessToast({
                        text: `${this.reservation.survey_data.title} successfully updated.`,
                    });
                }
            } catch (e) {
                this.reservation.inquiry_index = previous;
                const { generic } = formatResponseError(e);
                this.showErrorToast({
                    text: generic || `Could not change ${this.reservation.survey_data.title}.`,
                });
            } finally {
                this.$emit('loading', false);
                this.handleOutsideClick();
            }
        },
        refreshReservation() {
            this.$emit('refreshReservations');
        },
        showDeleteReferrer(id) {
            this.deleteReferrerId = id;
            this.$bvModal.show(this.deleteReferrerModalId);
        },
        async fetchReferrers() {
            try {
                this.referrerModalBusy = true;
                const { data } = await API.get(GUEST_REFERRER_LIST_ENDPOINT());
                this.referrers = data;
            } catch (e) {
                this.showErrorToast({ text: 'Could not load referrers.' });
            } finally {
                this.referrerModalBusy = false;
            }
        },
        async deleteReferrer() {
            try {
                this.referrerModalBusy = true;
                await API.delete(GUEST_REFERRER_DELETE_ENDPOINT(this.deleteReferrerId));
                this.fetchReferrers();
                this.refreshReservation();
            } catch (e) {
                this.showErrorToast({ text: 'Could not delete referrer.' });
            } finally {
                this.referrerModalBusy = false;
            }
        },
        async addReferrer() {
            try {
                this.$emit('loading', true);
                await API.patch(GUEST_RESERVATION_ENDPOINT(this.reservationId, this.guestId), {
                    referrer: this.newReferrer,
                });
                this.showSuccessToast({
                    text: 'Referrer successfully added.',
                });
                this.$bvModal.hide(this.addNewReferrerModalId);
                this.refreshReservation();
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.showErrorToast({
                    text: generic || 'Could not add referrer.',
                });
            } finally {
                this.$emit('loading', false);
            }
        },
    },
};
</script>
